import { FC } from 'react';
import { Box, Text, Icon, IconSize, Separator, SeparatorType } from '@bechtel/gds';
import { WeldDetailSpecificationProps, WeldFieldContentProps } from './types';
import { WeldCommentStyled } from './styled';
import { SpecificationDetailView } from '~/components';
import { t, Trans } from '@lingui/macro';
import { PersonaType } from '@bechtel/types';
import { filter, join } from 'lodash';
import { useName } from '@bechtel/client-common';

const WeldFieldContent: FC<WeldFieldContentProps> = ({ icon, label, value }) => {
  return (
    <Box alignItems={{ sm: 'center' }} py={{ sm: '400' }}>
      <Icon size={IconSize.MEDIUM} icon={icon} />
      <Box ml={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '400' }}>
          <Text size="300" color="grey.600">
            {label}
          </Text>
        </Box>
        <Text size="400" color="grey.1000" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
          {value}
        </Text>
      </Box>
    </Box>
  );
};

export const WeldDetailSpecification: FC<WeldDetailSpecificationProps> = ({ weld }) => {
  const { getFullName } = useName();
  const commodityAssignees =
    weld?.commodityAssignees && weld?.commodityAssignees.length > 0
      ? filter(weld.commodityAssignees, (item) => {
          return item.personaType == PersonaType.CRAFT;
        })
      : [];
  const stencils: string[] =
    commodityAssignees.length > 0
      ? commodityAssignees.map((item) => getFullName(item.assignee?.firstName, item.assignee?.lastName))
      : [];

  return (
    <Box flexDirection={{ sm: 'column' }} style={{ overflowY: 'auto' }}>
      <Box mx={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        {weld?.fitupRequest?.comments && (
          <WeldCommentStyled>
            <Box mb={{ sm: '300' }}>
              <Text size="300" color="grey.600">
                <Trans>Comments</Trans>
              </Text>
            </Box>
            <Text size="400" color="grey.1000" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
              {weld.fitupRequest.comments}
            </Text>
          </WeldCommentStyled>
        )}
        <WeldFieldContent
          icon="users"
          label={t`Stencil(s)`}
          value={stencils.length > 0 ? join(stencils, ', ') : 'None Assigned'}
        />
        <WeldFieldContent
          icon="fire"
          label={t`Weld Procedure(s)`}
          value={weld?.weldedWithProcedure?.procedure ? weld?.weldedWithProcedure?.procedure : t`None Assigned`}
        />
        <WeldFieldContent
          icon="location-dot"
          label={t`Location`}
          value={
            weld.location?.latitude
              ? `${weld.location?.latitude}, ${weld.location.longitude} ${
                  weld.locationDescription ? ` - ${weld.locationDescription}` : ''
                }`
              : 'None Assigned'
          }
        />
        <Separator type={SeparatorType.HORIZONTAL} color="grey.300" />
      </Box>
      <SpecificationDetailView
        commoditySections={weld.sections}
        showDocuments={false}
        workPackageTag=""
        commodityName=""
        commodityTag=""
      />
    </Box>
  );
};
